// useFilteredDevices.js
// Performance-optimized hook for device filtering with caching
// Part of Device Management Overhaul - Phase 4: Performance Optimization

import { useMemo, useCallback, useRef, useEffect } from 'react';

// Cache structure for filtered results
const FilterCache = {
  cache: new Map(),
  maxSize: 50,
  ttl: 60000, // 1 minute TTL

  generateKey(filters, searchTerm, devices) {
    return JSON.stringify({
      filters: filters || {},
      search: searchTerm || '',
      deviceCount: devices?.length || 0
    });
  },

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if cache entry is expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  },

  set(key, data) {
    // Implement LRU eviction if cache is too large
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  },

  clear() {
    this.cache.clear();
  }
};

// Optimized filtering function
const filterDevices = (devices, filters, searchTerm) => {
  let filtered = [...devices];

  // Apply search filter
  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(device => {
      // Search in multiple fields for better performance
      const searchableText = [
        device.friendlyName,
        device.id,
        device.deviceTypeModel?.friendlyName,
        device.locationModel?.friendlyName,
        device.config?.identifier?.lanIP,
        device.config?.identifier?.mac
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(lowerSearch);
    });
  }

  // Apply status filter (can be string or array)
  if (filters?.status && filters.status !== 'all') {
    const statusFilter = Array.isArray(filters.status) ? filters.status : [filters.status];

    // Skip if empty array or only contains 'all'
    if (statusFilter.length > 0 && !statusFilter.every(s => s === 'all')) {
      filtered = filtered.filter(device => {
        const deviceStatus = (device.config?.status || device.status || '').toLowerCase();

        return statusFilter.some(status => {
          const statusLower = (status || '').toLowerCase();
          if (statusLower === 'online') {
            return ['online', 'active', 'registered'].includes(deviceStatus);
          } else if (statusLower === 'offline') {
            return ['offline', 'inactive', 'disconnected'].includes(deviceStatus);
          }
          return deviceStatus === statusLower;
        });
      });
    }
  }

  // Apply device type filter (can be string 'deviceType' or array 'deviceTypeId')
  if (filters?.deviceType && filters.deviceType !== 'all') {
    filtered = filtered.filter(device => {
      const deviceType = (device.deviceTypeModel?.type || device.deviceTypeModel?.friendlyName || '').toLowerCase();
      return deviceType.includes(filters.deviceType.toLowerCase());
    });
  }

  // Apply device type ID filter (array of IDs from advanced filters)
  if (filters?.deviceTypeId && Array.isArray(filters.deviceTypeId) && filters.deviceTypeId.length > 0) {
    filtered = filtered.filter(device => {
      const deviceTypeId = device.deviceTypeModel?.id || device.deviceType;
      return filters.deviceTypeId.includes(deviceTypeId);
    });
  }

  // Apply location filter (string)
  if (filters?.location && filters.location !== 'all') {
    filtered = filtered.filter(device => {
      const locationId = device.locationModel?.id || device.deviceLocation || device.locationId;
      return String(locationId) === String(filters.location);
    });
  }

  // Apply location ID filter (array of IDs from advanced filters)
  if (filters?.locationId && Array.isArray(filters.locationId) && filters.locationId.length > 0) {
    filtered = filtered.filter(device => {
      const locationId = device.locationModel?.id || device.deviceLocation || device.locationId;
      return filters.locationId.includes(locationId);
    });
  }

  // Apply connection status filter (linked/unlinked)
  if (filters?.connection) {
    if (filters.connection === 'unlinked') {
      filtered = filtered.filter(device =>
        !device.parentDevice && !device.parentDeviceId && !device.locationModel && !device.deviceLocation
      );
    } else if (filters.connection === 'linked') {
      filtered = filtered.filter(device =>
        device.parentDevice || device.parentDeviceId || device.locationModel || device.deviceLocation
      );
    }
  }

  // Apply last seen filter
  if (filters?.lastSeen) {
    const now = Date.now();
    filtered = filtered.filter(device => {
      if (!device.last_seen) return filters.lastSeen === 'never';

      const lastSeenTime = new Date(device.last_seen).getTime();
      const hoursSince = (now - lastSeenTime) / (1000 * 60 * 60);

      switch (filters.lastSeen) {
        case '1h':
          return hoursSince <= 1;
        case '24h':
          return hoursSince <= 24;
        case '7d':
          return hoursSince <= 168;
        case '30d':
          return hoursSince <= 720;
        case 'never':
          return false;
        default:
          return true;
      }
    });
  }

  // Apply custom filters
  if (filters?.custom) {
    Object.entries(filters.custom).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(device => {
          const customData = device.config?.customData || [];
          const customField = customData.find(field => field.key === key);
          return customField?.value?.toLowerCase().includes(value.toLowerCase());
        });
      }
    });
  }

  return filtered;
};

// Sort cache to prevent re-sorting unchanged data
const SortCache = {
  lastInput: null,
  lastSortBy: null,
  lastSortOrder: null,
  lastResult: null,

  get(devices, sortBy, sortOrder) {
    // Check if inputs are the same (reference equality for devices array)
    if (
      this.lastInput === devices &&
      this.lastSortBy === sortBy &&
      this.lastSortOrder === sortOrder &&
      this.lastResult
    ) {
      return this.lastResult;
    }
    return null;
  },

  set(devices, sortBy, sortOrder, result) {
    this.lastInput = devices;
    this.lastSortBy = sortBy;
    this.lastSortOrder = sortOrder;
    this.lastResult = result;
  },

  clear() {
    this.lastInput = null;
    this.lastSortBy = null;
    this.lastSortOrder = null;
    this.lastResult = null;
  }
};

// Main hook
export function useFilteredDevices(devices, filters, searchTerm) {
  const lastComputedRef = useRef(null);
  const computationTimeRef = useRef(0);

  // Clear cache when devices change significantly
  useEffect(() => {
    const deviceCount = devices?.length || 0;
    if (lastComputedRef.current && Math.abs(lastComputedRef.current.deviceCount - deviceCount) > 100) {
      FilterCache.clear();
      SortCache.clear();
    }
  }, [devices]);

  // Memoized filtering with caching
  const filteredDevices = useMemo(() => {
    const startTime = performance.now();

    // Generate cache key
    const cacheKey = FilterCache.generateKey(filters, searchTerm, devices);

    // Check cache first
    const cached = FilterCache.get(cacheKey);
    if (cached) {
      computationTimeRef.current = performance.now() - startTime;
      return cached;
    }

    // Perform filtering
    const filtered = filterDevices(devices, filters, searchTerm);

    // Store in cache
    FilterCache.set(cacheKey, filtered);

    // Track computation time
    computationTimeRef.current = performance.now() - startTime;
    lastComputedRef.current = {
      deviceCount: devices.length,
      resultCount: filtered.length
    };

    return filtered;
  }, [devices, filters, searchTerm]);

  // Performance metrics - use ref to avoid re-render on metrics change
  const performanceMetrics = useMemo(() => ({
    computationTime: computationTimeRef.current,
    totalDevices: devices.length,
    filteredCount: filteredDevices.length,
    cacheSize: FilterCache.cache.size
  }), [devices.length, filteredDevices.length]);

  // Clear cache function
  const clearCache = useCallback(() => {
    FilterCache.clear();
    SortCache.clear();
  }, []);

  // Optimized sort function with caching - returns same reference if inputs unchanged
  const sortDevices = useCallback((devicesToSort, sortBy, sortOrder) => {
    // Check sort cache first
    const cached = SortCache.get(devicesToSort, sortBy, sortOrder);
    if (cached) {
      return cached;
    }

    const sorted = [...devicesToSort].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'name':
          aVal = (a.friendlyName || `Device ${a.id}`).toLowerCase();
          bVal = (b.friendlyName || `Device ${b.id}`).toLowerCase();
          break;
        case 'type':
          aVal = (a.deviceTypeModel?.friendlyName || '').toLowerCase();
          bVal = (b.deviceTypeModel?.friendlyName || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.config?.status || a.status || '').toLowerCase();
          bVal = (b.config?.status || b.status || '').toLowerCase();
          break;
        case 'lastSeen':
          aVal = a.last_seen ? new Date(a.last_seen).getTime() : 0;
          bVal = b.last_seen ? new Date(b.last_seen).getTime() : 0;
          break;
        case 'location':
          aVal = (a.locationModel?.friendlyName || '').toLowerCase();
          bVal = (b.locationModel?.friendlyName || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    // Cache the result
    SortCache.set(devicesToSort, sortBy, sortOrder, sorted);

    return sorted;
  }, []);

  return {
    filteredDevices,
    performanceMetrics,
    clearCache,
    sortDevices
  };
}

export default useFilteredDevices;